/**
 * SharePoint Source Registration Form
 * Placeholder component for SharePoint source registration
 */

import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { registerSharePointSource } from '../../api/sources';

interface SharePointFormProps {
  projectId: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function SharePointForm({ projectId, onSuccess, onClose }: SharePointFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [spName, setSpName] = useState('');
  const [spDescription, setSpDescription] = useState('');
  const [spTenantId, setSpTenantId] = useState('');
  const [spClientId, setSpClientId] = useState('');
  const [spClientSecret, setSpClientSecret] = useState('');
  const [spSiteUrl, setSpSiteUrl] = useState('');
  const [spLibraryPath, setSpLibraryPath] = useState('');
  const [spSyncFrequency, setSpSyncFrequency] = useState<'hourly' | '6hourly' | 'daily'>('hourly');
  
  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    
    try {
      if (!spName || !spTenantId || !spClientId || !spClientSecret || !spSiteUrl) {
        setError('All required fields must be filled');
        setLoading(false);
        return;
      }
      
      await registerSharePointSource({
        project_id: projectId,
        name: spName,
        description: spDescription || undefined,
        credentials: {
          tenant_id: spTenantId,
          client_id: spClientId,
          client_secret: spClientSecret,
          site_url: spSiteUrl,
        },
        library_path: spLibraryPath || undefined,
        watch_for_changes: true,
        recursive: true,
        sync_frequency: spSyncFrequency,
      });
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to register SharePoint source');
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p className="text-sm text-purple-900">
          Connect to Microsoft SharePoint to sync documents.
          Requires Azure AD app registration with SharePoint permissions.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source Name *
        </label>
        <Input
          value={spName}
          onChange={(e) => setSpName(e.target.value)}
          placeholder="e.g., SharePoint Documents, Team Site"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SharePoint Site URL *
        </label>
        <Input
          value={spSiteUrl}
          onChange={(e) => setSpSiteUrl(e.target.value)}
          placeholder="https://contoso.sharepoint.com/sites/MySite"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Azure AD Tenant ID *
        </label>
        <Input
          value={spTenantId}
          onChange={(e) => setSpTenantId(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Azure AD Client ID *
        </label>
        <Input
          value={spClientId}
          onChange={(e) => setSpClientId(e.target.value)}
          placeholder="Application (client) ID"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Azure AD Client Secret *
        </label>
        <Input
          type="password"
          value={spClientSecret}
          onChange={(e) => setSpClientSecret(e.target.value)}
          placeholder="Client secret value"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Document Library Path (optional)
        </label>
        <Input
          value={spLibraryPath}
          onChange={(e) => setSpLibraryPath(e.target.value)}
          placeholder="e.g., /Shared Documents/Reports (leave empty for entire site)"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sync Frequency
        </label>
        <select
          value={spSyncFrequency}
          onChange={(e) => setSpSyncFrequency(e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="hourly">Hourly</option>
          <option value="6hourly">Every 6 Hours</option>
          <option value="daily">Daily</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <Input
          value={spDescription}
          onChange={(e) => setSpDescription(e.target.value)}
          placeholder="Optional description"
        />
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Registering...' : 'Register Source'}
        </Button>
      </div>
    </div>
  );
}













